// src/components/admin/PedidosData.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync,
    faEye,
    faFileExcel,
    faFilePdf,
    faClipboardList,
    faTimes,
    faFilter,
    faChevronLeft,
    faChevronRight,
    faExclamationTriangle,
    faCheckCircle,
    faTimesCircle,
    faClock,
    faBox,
    faShoppingCart
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import baseURL from '../../url';
import moneda from '../../moneda';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function PedidosData() {
    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [estadisticas, setEstadisticas] = useState({});
    const [paginacion, setPaginacion] = useState({});

    // Modal
    const [modalVisible, setModalVisible] = useState(false);
    const [pedido, setPedido] = useState({});
    const [pedidoItems, setPedidoItems] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [nuevoEstado, setNuevoEstado] = useState('');
    const [stockAlertas, setStockAlertas] = useState([]);

    // Filtros y paginación
    const [filtroId, setFiltroId] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroDesde, setFiltroDesde] = useState('');
    const [filtroHasta, setFiltroHasta] = useState('');
    const [paginaActual, setPaginaActual] = useState(1);
    const [limite] = useState(20);

    // Toasts
    const [toasts, setToasts] = useState([]);
    const showToast = (message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3600);
    };

    useEffect(() => {
        cargarPedidos();
    }, [filtroEstado, filtroDesde, filtroHasta, paginaActual]);

    const cargarPedidos = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                include_items: 'true',
                limite: limite.toString(),
                offset: ((paginaActual - 1) * limite).toString()
            });

            if (filtroEstado) params.append('estado', filtroEstado);
            if (filtroDesde) params.append('desde', filtroDesde);
            if (filtroHasta) params.append('hasta', filtroHasta);

            const res = await fetch(`${baseURL}/pedidoGet.php?${params}`, { method: 'GET' });
            const data = await res.json();
            
            if (data.error) {
                showToast(data.error, 'error');
                return;
            }

            setPedidos(data.pedidos || []);
            setEstadisticas(data.estadisticas || {});
            setPaginacion(data.paginacion || {});
        } catch (e) {
            console.error(e);
            showToast('Error al cargar pedidos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const recargar = () => {
        cargarPedidos();
        showToast('Datos actualizados', 'info');
    };

    // ---------- Helpers ----------
    const parseProductosSnapshot = (p) => {
        try {
            if (Array.isArray(p)) return p;
            return JSON.parse(p || '[]');
        } catch {
            return [];
        }
    };

    const formatMoney = (v) => {
        const num = Number(v || 0);
        return `${moneda} ${num.toLocaleString('es-CO', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const estadoBadge = (estado) => {
        const e = (estado || '').toLowerCase();
        if (e === 'cancelado' || e === 'rechazado') return 'error';
        if (e === 'pendiente') return 'warning';
        if (e === 'pagado' || e === 'entregado') return 'success';
        return '';
    };

    const getEstadoIcon = (estado) => {
        const e = (estado || '').toLowerCase();
        switch (e) {
            case 'pendiente': return faClock;
            case 'pagado': return faCheckCircle;
            case 'entregado': return faBox;
            case 'cancelado':
            case 'rechazado': return faTimesCircle;
            default: return faShoppingCart;
        }
    };

    const isLocked = (p) => (p?.estado || '').toLowerCase() === 'cancelado';

    // ---------- Modal ----------
    const abrirModal = async (item) => {
        setPedido(item);
        setNuevoEstado(item?.estado || '');
        setModalVisible(true);
        setStockAlertas([]);
        
        // Cargar detalles completos del pedido
        await cargarDetallesPedido(item.idPedido);
    };

    const cargarDetallesPedido = async (idPedido) => {
        setItemsLoading(true);
        try {
            const res = await fetch(`${baseURL}/pedidoItemsGet.php?idPedido=${idPedido}`);
            const data = await res.json();
            
            if (data.error) {
                showToast(data.error, 'error');
                return;
            }

            setPedidoItems(data.items || []);
            setStockAlertas(data.alertas?.stock_warnings || []);
            
            // Mostrar alertas de stock si las hay
            if (data.alertas?.tiene_alertas) {
                showToast(`Hay ${data.alertas.stock_warnings.length} productos con stock insuficiente`, 'warning');
            }
        } catch (e) {
            console.error('Error cargando detalles:', e);
            showToast('No se pudo cargar el detalle de productos', 'warning');
        } finally {
            setItemsLoading(false);
        }
    };

    const cerrarModal = () => {
        setModalVisible(false);
        setPedido({});
        setPedidoItems([]);
        setNuevoEstado('');
        setStockAlertas([]);
    };

    const handleUpdateEstado = async (idPedido, estadoDestino) => {
        try {
            const res = await fetch(`${baseURL}/pedidoPut.php?idPedido=${idPedido}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado: estadoDestino }),
            });
            const data = await res.json();
            if (data?.error) return showToast(data.error, 'error');
            showToast(data?.mensaje || 'Pedido actualizado', 'success');
            await cargarPedidos();
            setPedido(prev => ({ ...prev, estado: estadoDestino }));
        } catch (e) {
            showToast('Error al actualizar el pedido', 'error');
        }
    };

    const cancelarConReintegro = async (idPedido) => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Cancelar pedido?',
            text: 'Se reintegrará el stock de los productos y el pedido quedará bloqueado.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, cancelar',
            cancelButtonText: 'No',
        });
        if (!isConfirmed) return;
        await handleUpdateEstado(idPedido, 'Cancelado');
    };

    const handleGuardarEstado = async (idPedido) => {
        if (isLocked(pedido)) return;
        const destino = nuevoEstado || pedido.estado || 'Pendiente';
        await handleUpdateEstado(idPedido, destino);
        cerrarModal();
    };

    // ---------- Filtros ----------
    const filtrados = useMemo(() => {
        return pedidos.filter(item => {
            const idMatch = !filtroId || (item.idPedido ?? '').toString().includes(filtroId);
            return idMatch;
        });
    }, [pedidos, filtroId]);

    const totalFiltrado = useMemo(
        () => filtrados.reduce((acc, it) => acc + (parseFloat(it.total) || 0), 0),
        [filtrados]
    );

    const limpiarFiltros = () => {
        setFiltroId('');
        setFiltroEstado('');
        setFiltroDesde('');
        setFiltroHasta('');
        setPaginaActual(1);
    };

    // ---------- Paginación ----------
    const cambiarPagina = (nuevaPagina) => {
        if (nuevaPagina >= 1 && nuevaPagina <= (paginacion.total_paginas || 1)) {
            setPaginaActual(nuevaPagina);
        }
    };

    // ---------- Exportaciones ----------
    const descargarExcel = () => {
        let totalGeneral = 0;
        const data = filtrados.map(item => {
            const total = parseFloat(item.total) || 0;
            totalGeneral += total;

            const productos = item.items || parseProductosSnapshot(item.productos);
            const infoProductos = productos.map(p => {
                const precio = p.precio_unitario ?? p.precio ?? 0;
                const titulo = p.titulo_producto || p.titulo || '—';
                const variantes = [p.color, p.talla].filter(Boolean).join(' / ');
                return `${titulo}${variantes ? ' (' + variantes + ')' : ''} - ${moneda}${precio} - x${p.cantidad || 0}`;
            }).join('\n');

            return {
                'ID Pedido': item.idPedido,
                'Estado': item.estado,
                'Nombre': item.nombre,
                'Número ID': item.numero_identificacion || '',
                'Email': item.email,
                'Teléfono': item.telefono,
                'Pago': item.pago,
                'Entrega': item.entrega,
                'Dirección Entrega': item.direccion_entrega || '',
                'Nota': item.nota || '',
                'Código': item.codigo,
                'Total': `${moneda} ${total.toFixed(2)}`,
                'Fecha': item.createdAt,
                'Productos': infoProductos,
            };
        });

        data.push({
            'ID Pedido': '',
            'Estado': '',
            'Nombre': '',
            'Número ID': '',
            'Email': '',
            'Teléfono': '',
            'Pago': '',
            'Entrega': '',
            'Dirección Entrega': '',
            'Nota': '',
            'Código': 'TOTAL GENERAL:',
            'Total': `${moneda} ${totalGeneral.toFixed(2)}`,
            'Fecha': '',
            'Productos': '',
        });

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Pedidos');
        XLSX.writeFile(wb, 'pedidos.xlsx');
        showToast('Excel generado correctamente', 'success');
    };

    const descargarPDF = () => {
        const pdf = new jsPDF('landscape');
        pdf.text('Lista de Pedidos', 10, 10);

        const columns = [
            'ID', 'Estado', 'Nombre', 'N° ID', 'Email', 'Teléfono', 'Pago',
            'Entrega', 'Total', 'Fecha'
        ];
        let totalGeneral = 0;

        const body = filtrados.map(item => {
            const total = parseFloat(item.total) || 0;
            totalGeneral += total;
            return [
                item.idPedido, 
                item.estado, 
                item.nombre, 
                item.numero_identificacion || '',
                item.email, 
                item.telefono, 
                item.pago, 
                item.entrega, 
                `${moneda} ${total.toFixed(2)}`, 
                item.fecha_formateada || item.createdAt
            ];
        });

        body.push(['', '', '', '', '', '', '', 'TOTAL GENERAL:', `${moneda} ${totalGeneral.toFixed(2)}`, '']);
        pdf.autoTable({ head: [columns], body, styles: { fontSize: 8 } });
        pdf.save('pedidos.pdf');
        showToast('PDF generado correctamente', 'success');
    };

    const handleDownloadPDF = () => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        let y = 15;

        // Título
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Detalle del Pedido', 10, y);
        y += 10;

        // Datos del pedido
        const p = pedido || {};
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        const detalle = [
            ['ID Pedido', `${p.idPedido || ''}`],
            ['Estado', `${p.estado || ''}`],
            ['Código', `${p.codigo || ''}`],
            ['Nombre Cliente', `${p.nombre || ''}`],
            ['Número de Identificación', `${p.numero_identificacion || ''}`],
            ['Email', `${p.email || ''}`],
            ['Teléfono', `${p.telefono || ''}`],
            ['Método de Pago', `${p.pago || ''}`],
            ['Tipo de Entrega', `${p.entrega || ''}`],
            ['Dirección de Entrega', `${p.direccion_entrega || 'No especificada'}`],
            ['Fecha de Entrega', `${p.fecha_entrega || 'No especificada'}`],
            ['Nota', `${p.nota || 'Sin observaciones'}`],
            ['Total del Pedido', `${formatMoney(p.total || 0)}`],
            ['Fecha de Creación', `${p.fecha_formateada || p.createdAt || ''}`],
        ];

        pdf.autoTable({
            startY: y,
            head: [['Campo', 'Valor']],
            body: detalle,
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: [53, 92, 125] },
            columnStyles: {
                0: { fontStyle: 'bold', cellWidth: 50 },
                1: { cellWidth: 130 }
            }
        });
        
        y = pdf.lastAutoTable.finalY + 10;

        // Productos
        if (pedidoItems.length > 0) {
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Productos del Pedido', 10, y);
            y += 5;

            const tablaProductos = pedidoItems.map((pr, index) => {
                const titulo = pr.titulo_producto || pr.titulo || 'Producto sin nombre';
                const variantes = [pr.color, pr.talla].filter(Boolean).join(' / ') || '—';
                const precio = Number(pr.precio_unitario || 0);
                const cant = Number(pr.cantidad || 0);
                const descuento = Number(pr.precio_descuento || 0);
                const subtotal = (precio * cant) - descuento;
                
                return [
                    index + 1,
                    titulo,
                    variantes,
                    `${moneda}${precio.toFixed(2)}`,
                    cant,
                    descuento > 0 ? `${moneda}${descuento.toFixed(2)}` : '—',
                    `${moneda}${subtotal.toFixed(2)}`
                ];
            });

            // Calcular totales
            const subtotalGeneral = pedidoItems.reduce((acc, pr) => {
                const precio = Number(pr.precio_unitario || 0);
                const cant = Number(pr.cantidad || 0);
                const descuento = Number(pr.precio_descuento || 0);
                return acc + ((precio * cant) - descuento);
            }, 0);

            tablaProductos.push([
                '', '', '', '', '', 'TOTAL:', `${moneda}${subtotalGeneral.toFixed(2)}`
            ]);

            pdf.autoTable({
                startY: y,
                head: [['#', 'Producto', 'Variante', 'Precio Unit.', 'Cant.', 'Descuento', 'Subtotal']],
                body: tablaProductos,
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [53, 92, 125] },
                columnStyles: {
                    0: { cellWidth: 10 },
                    1: { cellWidth: 60 },
                    2: { cellWidth: 30 },
                    3: { cellWidth: 25 },
                    4: { cellWidth: 15 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 25 }
                }
            });
        }

        // Alertas de stock si las hay
        if (stockAlertas.length > 0) {
            y = pdf.lastAutoTable.finalY + 10;
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(255, 0, 0);
            pdf.text('ALERTAS DE STOCK', 10, y);
            y += 5;

            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal');
            
            const alertasData = stockAlertas.map(alerta => [
                alerta.producto,
                alerta.variante,
                alerta.cantidad_pedida,
                alerta.stock_disponible,
                alerta.diferencia
            ]);

            pdf.autoTable({
                startY: y,
                head: [['Producto', 'Variante', 'Pedido', 'Stock', 'Faltante']],
                body: alertasData,
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [239, 68, 68] }
            });
        }

        pdf.save(`pedido_${p.idPedido || 'detalle'}.pdf`);
        showToast('PDF del pedido generado', 'success');
    };

    // ---------- UI ----------
    return (
        <div>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className="product-toast-container">
                    {toasts.map(t => (
                        <div key={t.id} className={`product-toast ${t.type}`}>
                            <div className="toast-icon">
                                {t.type === 'success' && '✅'}
                                {t.type === 'error' && '❌'}
                                {t.type === 'warning' && '⚠️'}
                                {t.type === 'info' && 'ℹ️'}
                            </div>
                            <div className="toast-content">
                                <div className="toast-title">{t.message}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Header + stats */}
            <div className="global-component-header" style={{ padding: '12px 16px' }}>
                <div className="flex-between" style={{ width: '100%' }}>
                    <div className="flex-center" style={{ gap: '16px' }}>
                        <div className="global-component-title">
                            <FontAwesomeIcon icon={faClipboardList} />
                            Gestión de Pedidos
                        </div>

                        <div className="global-component-stats">
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{estadisticas.total_pedidos || 0}</span>
                                <span className="global-stat-label">Total</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{estadisticas.pendientes || 0}</span>
                                <span className="global-stat-label">Pendientes</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{estadisticas.entregados || 0}</span>
                                <span className="global-stat-label">Entregados</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{formatMoney(estadisticas.total_ventas || 0)}</span>
                                <span className="global-stat-label">Total Ventas</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex-center" style={{ gap: 8 }}>
                        <button className="btn-product-primary compact flex-center" onClick={descargarExcel} title="Exportar Excel">
                            <FontAwesomeIcon icon={faFileExcel} />
                        </button>
                        <button className="btn-product-primary compact flex-center" onClick={descargarPDF} title="Exportar PDF">
                            <FontAwesomeIcon icon={faFilePdf} />
                        </button>
                        <button className="btn-product-secondary compact flex-center" onClick={recargar} title="Actualizar">
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtros */}
            <div className="inputsGrap" style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 12 }}>
                    {/* <div className="product-form-group">
                        <label className="product-form-label">Filtrar por ID</label>
                        <input
                            className="product-form-input"
                            placeholder="Ej: 102"
                            value={filtroId}
                            onChange={(e) => setFiltroId(e.target.value)}
                        />
                    </div> */}
                    <div className="product-form-group">
                        <label className="product-form-label">Estado</label>
                        <select
                            className="product-form-input"
                            value={filtroEstado}
                            onChange={(e) => setFiltroEstado(e.target.value)}
                        >
                            <option value="">Todos</option>
                            <option value="Pendiente">Pendiente</option>
                            <option value="Pagado">Pagado</option>
                            <option value="Entregado">Entregado</option>
                            <option value="Cancelado">Cancelado</option>
                            <option value="Rechazado">Rechazado</option>
                        </select>
                    </div>
                    <div className="product-form-group">
                        <label className="product-form-label">Desde</label>
                        <input
                            type="date"
                            className="product-form-input"
                            value={filtroDesde}
                            onChange={(e) => setFiltroDesde(e.target.value)}
                        />
                    </div>
                    <div className="product-form-group">
                        <label className="product-form-label">Hasta</label>
                        <input
                            type="date"
                            className="product-form-input"
                            value={filtroHasta}
                            onChange={(e) => setFiltroHasta(e.target.value)}
                        />
                    </div>
                    <div className="product-form-group" style={{ alignSelf: 'end' }}>
                        <button className="btn-product-secondary" onClick={limpiarFiltros}>
                            <FontAwesomeIcon icon={faFilter} style={{ marginRight: 8 }} />
                            Limpiar filtros
                        </button>
                    </div>
                </div>
            </div>

            {/* Paginación superior */}
            {paginacion.total_paginas > 1 && (
                <div className="flex-between" style={{ marginBottom: 16, padding: '0 16px' }}>
                    <div className="text-muted">
                        Página {paginacion.pagina_actual || 1} de {paginacion.total_paginas || 1} 
                        ({paginacion.total_registros || 0} registros)
                    </div>
                    <div className="flex-center" style={{ gap: 8 }}>
                        <button 
                            className="btn-product-secondary" 
                            onClick={() => cambiarPagina(paginaActual - 1)}
                            disabled={!paginacion.tiene_anterior}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span style={{ padding: '0 12px', fontWeight: 600 }}>
                            {paginaActual}
                        </span>
                        <button 
                            className="btn-product-secondary" 
                            onClick={() => cambiarPagina(paginaActual + 1)}
                            disabled={!paginacion.tiene_siguiente}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
            )}

            {/* Tabla (desktop) */}
            <div className="global-table-container desktop-only">
                {loading ? (
                    <div className="global-loading-state">
                        <FontAwesomeIcon icon={faSync} size="2x" />
                        <div>Cargando pedidos...</div>
                    </div>
                ) : filtrados.length > 0 ? (
                    <table className="global-table">
                        <thead>
                            <tr>
                                <th>Pedido</th>
                                <th>Estado</th>
                                <th>Cliente</th>
                                <th>Contacto</th>
                                <th>Entrega</th>
                                <th>Pago</th>
                                {/* <th>Items</th> */}
                                <th>Total</th>
                                <th>Fecha</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtrados.map((item, index) => (
                                <tr key={item.idPedido} style={{ animationDelay: `${index * 0.05}s` }}>
                                    <td>
                                        <div>
                                            <strong>#{item.idPedido}</strong>
                                            <br />
                                            <small className="text-muted">{item.codigo}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`global-indicator ${estadoBadge(item.estado)}`}>
                                            <FontAwesomeIcon icon={getEstadoIcon(item.estado)} />
                                            {item.estado || '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <div>
                                            <strong>{item.nombre}</strong>
                                            <br />
                                            <small className="text-muted">{item.numero_identificacion || 'Sin ID'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div>
                                            <div>{item.email}</div>
                                            <small className="text-muted">{item.telefono}</small>
                                        </div>
                                    </td>
                                    <td>{item.entrega}</td>
                                    <td>{item.pago}</td>
                                    {/* <td>
                                        <span className="global-indicator">
                                            {item.total_items || (item.items ? item.items.length : 0)} items
                                        </span>
                                    </td> */}
                                    <td style={{ fontWeight: 700 }}>{formatMoney(item.total)}</td>
                                    <td>{item.fecha_formateada || item.createdAt}</td>
                                    <td>
                                        <div className="global-table-actions">
                                            <button className="global-btn-edit" title="Ver / Gestionar" onClick={() => abrirModal(item)}>
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="global-empty-state">
                        <FontAwesomeIcon icon={faClipboardList} size="3x" />
                        <div className="global-empty-title">No hay pedidos</div>
                        <div className="global-empty-description">Ajusta los filtros o recarga la lista.</div>
                    </div>
                )}
            </div>

            {/* Tarjetas (mobile) */}
            {!loading && (
                <div className="mobile-only card-list">
                    {filtrados.length === 0 ? (
                        <div className="global-empty-state">
                            <FontAwesomeIcon icon={faClipboardList} size="3x" />
                            <div className="global-empty-title">No hay pedidos</div>
                            <div className="global-empty-description">Ajusta los filtros o recarga la lista.</div>
                        </div>
                    ) : (
                        filtrados.map((item) => {
                            const firstImg = (item.items && item.items[0]?.imagen_final)
                                || (item.items && item.items[0]?.imagen)
                                || (parseProductosSnapshot(item.productos)[0]?.imagen)
                                || '';
                            return (
                                <div className="product-card" key={item.idPedido}>
                                    <div className="card-header">
                                        <div className="card-image-container">
                                            {firstImg ? (
                                                <img className="card-image" src={firstImg} alt="producto" />
                                            ) : (
                                                <div className="card-image-placeholder">
                                                    <FontAwesomeIcon icon={faShoppingCart} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="card-info">
                                            <div className="card-title">Pedido #{item.idPedido}</div>
                                            <div>
                                                <span className={`global-indicator ${estadoBadge(item.estado)}`}>
                                                    <FontAwesomeIcon icon={getEstadoIcon(item.estado)} />
                                                    {item.estado}
                                                </span>
                                            </div>
                                            <div className="text-muted">
                                                {item.nombre} · {item.telefono}
                                            </div>
                                            <div className="text-muted">
                                                {item.total_items || (item.items ? item.items.length : 0)} items · {item.pago}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-footer">
                                        <div className="card-stock">
                                            <span className="stock-label">Total:</span>
                                            <span className="stock-value">{formatMoney(item.total)}</span>
                                        </div>
                                        <div className="card-actions">
                                            <button className="global-btn-edit" title="Ver / Gestionar" onClick={() => abrirModal(item)}>
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Modal de detalle / edición */}
            {modalVisible && (
                <div className="product-modal-backdrop" style={{ height: '100svh' }}>
                    <div
                        className="product-modal-container"
                        style={{
                            maxWidth: 1200,
                            height: '100svh',
                            maxHeight: '100svh',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div className="product-modal-header">
                            <div className="product-modal-title">
                                <FontAwesomeIcon icon={faClipboardList} />
                                Pedido #{pedido.idPedido} - {pedido.codigo}
                            </div>
                            <button className="product-close-button" onClick={cerrarModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div
                            className="product-modal-body"
                            style={{
                                flex: 1,
                                maxHeight: 'calc(100svh - 160px)',
                                overflowY: 'auto',
                            }}
                        >
                            {/* Alertas de stock */}
                            {stockAlertas.length > 0 && (
                                <div className="product-section-card mb-24" style={{ border: '2px solid #ef4444' }}>
                                    <div className="product-section-header">
                                        <div className="product-section-title" style={{ color: '#ef4444' }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            Alertas de Stock ({stockAlertas.length})
                                        </div>
                                    </div>
                                    <div className="product-section-content">
                                        {stockAlertas.map((alerta, index) => (
                                            <div key={index} className="flex-between" style={{ 
                                                padding: '8px 12px', 
                                                background: '#fef2f2', 
                                                borderRadius: '6px', 
                                                marginBottom: '8px',
                                                border: '1px solid #fecaca'
                                            }}>
                                                <div>
                                                    <strong>{alerta.producto}</strong> ({alerta.variante})
                                                </div>
                                                <div className="text-muted">
                                                    Pedido: {alerta.cantidad_pedida} | Stock: {alerta.stock_disponible} | 
                                                    <span style={{ color: '#ef4444', fontWeight: 600 }}>
                                                        Falta: {alerta.diferencia}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Datos del pedido */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title">Información del Pedido</div>
                                </div>
                                <div className="product-section-content">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
                                        {[
                                            ['ID Pedido', `#${pedido.idPedido}`],
                                            ['Código', pedido.codigo],
                                            ['Estado Actual', pedido.estado],
                                            ['Cliente', pedido.nombre],
                                            ['Identificación', pedido.numero_identificacion || 'No proporcionada'],
                                            ['Email', pedido.email],
                                            ['Teléfono', pedido.telefono],
                                            ['Método de Pago', pedido.pago],
                                            ['Tipo de Entrega', pedido.entrega],
                                            ['Dirección de Entrega', pedido.direccion_entrega || 'No especificada'],
                                            ['Fecha de Entrega', pedido.fecha_entrega || 'No especificada'],
                                            ['Observaciones', pedido.nota || 'Sin observaciones'],
                                            ['Total del Pedido', formatMoney(pedido.total)],
                                            ['Fecha de Creación', pedido.fecha_formateada || pedido.createdAt],
                                        ].map(([label, value]) => (
                                            <div className="product-form-group" key={label}>
                                                <label className="product-form-label">{label}</label>
                                                <input 
                                                    className="product-form-input" 
                                                    value={value || ''} 
                                                    disabled 
                                                    style={label === 'Total del Pedido' ? { fontWeight: 'bold', color: '#355C7D' } : {}}
                                                />
                                            </div>
                                        ))}

                                        {/* Estado editable */}
                                        <div className="product-form-group">
                                            <label className="product-form-label">Cambiar Estado</label>
                                            <select
                                                className="product-form-input"
                                                value={nuevoEstado || pedido.estado || ''}
                                                onChange={(e) => setNuevoEstado(e.target.value)}
                                                disabled={isLocked(pedido)}
                                            >
                                                <option value={pedido.estado || ''}>{pedido.estado || '—'}</option>
                                                <option value="Pendiente">Pendiente</option>
                                                <option value="Pagado">Pagado</option>
                                                <option value="Entregado">Entregado</option>
                                                <option value="Cancelado">Cancelado</option>
                                                <option value="Rechazado">Rechazado</option>
                                            </select>
                                            {isLocked(pedido) && (
                                                <small style={{ color: '#ef4444', fontSize: '0.8rem' }}>
                                                    Este pedido está cancelado y no admite cambios.
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Acciones rápidas */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title">Acciones Rápidas</div>
                                </div>
                                <div className="product-section-content" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                    <button
                                        className="btn-product-secondary"
                                        onClick={() => handleUpdateEstado(pedido.idPedido, 'Pagado')}
                                        disabled={isLocked(pedido)}
                                        style={{ background: '#10b981' }}
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />
                                        Marcar como Pagado
                                    </button>
                                    <button
                                        className="btn-product-secondary"
                                        onClick={() => handleUpdateEstado(pedido.idPedido, 'Entregado')}
                                        disabled={isLocked(pedido)}
                                        style={{ background: '#3b82f6' }}
                                    >
                                        <FontAwesomeIcon icon={faBox} style={{ marginRight: 6 }} />
                                        Marcar como Entregado
                                    </button>
                                    <button
                                        className="btn-product-secondary"
                                        onClick={() => cancelarConReintegro(pedido.idPedido)}
                                        disabled={isLocked(pedido)}
                                        style={{ background: '#ef4444' }}
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: 6 }} />
                                        Cancelar y Reintegrar Stock
                                    </button>
                                </div>
                            </div>

                            {/* Productos del pedido */}
                            <div className="product-section-card">
                                <div className="product-section-header">
                                    <div className="product-section-title">
                                        Productos del Pedido ({pedidoItems.length} items)
                                    </div>
                                </div>
                                <div className="product-section-content">
                                    {itemsLoading ? (
                                        <div className="global-loading-state">
                                            <div className="loading-spinner"></div>
                                            Cargando productos...
                                        </div>
                                    ) : pedidoItems.length > 0 ? (
                                        <div className="cardsProductData">
                                            {pedidoItems.map((pr, idx) => {
                                                const titulo = pr.titulo_producto || pr.titulo || 'Producto';
                                                const color = pr.color || '';
                                                const talla = pr.talla || '';
                                                const precio = Number(pr.precio_unitario || 0);
                                                const cantidad = Number(pr.cantidad || 0);
                                                const descuento = Number(pr.precio_descuento || 0);
                                                const subtotal = (precio * cantidad) - descuento;
                                                const idPT = pr.idProductoTalla || pr.id_producto_talla || null;
                                                const stockActual = pr.stock_actual || 0;
                                                const imagen = pr.imagen || '';

                                                // Verificar si hay problemas de stock
                                                const stockInsuficiente = stockActual < cantidad;

                                                return (
                                                    <div 
                                                        className="cardProductData" 
                                                        key={`${titulo}-${idx}`}
                                                        style={stockInsuficiente ? { 
                                                            border: '2px solid #ef4444', 
                                                            background: '#fef2f2' 
                                                        } : {}}
                                                    >
                                                        {imagen ? (
                                                            <img src={imagen} alt={titulo} />
                                                        ) : (
                                                            <div style={{
                                                                width: '5rem',
                                                                height: '5rem',
                                                                borderRadius: '10px',
                                                                background: '#f3f4f6',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#9ca3af'
                                                            }}>
                                                                <FontAwesomeIcon icon={faBox} size="2x" />
                                                            </div>
                                                        )}
                                                        <div className="cardProductDataText">
                                                            <h3>{titulo}</h3>
                                                            {(color || talla) && (
                                                                <h5>{[color, talla].filter(Boolean).join(' / ')}</h5>
                                                            )}
                                                            {idPT && (
                                                                <span>ID Variante: {idPT}</span>
                                                            )}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <strong>
                                                                    {formatMoney(precio)} 
                                                                    <span style={{ paddingLeft: '0.5rem' }}>x{cantidad}</span>
                                                                </strong>
                                                                {descuento > 0 && (
                                                                    <span style={{ 
                                                                        color: '#ef4444', 
                                                                        fontSize: '12px',
                                                                        textDecoration: 'line-through' 
                                                                    }}>
                                                                        -{formatMoney(descuento)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span style={{ 
                                                                fontWeight: 700, 
                                                                color: '#355C7D',
                                                                fontSize: '16px' 
                                                            }}>
                                                                {formatMoney(subtotal)}
                                                            </span>
                                                            
                                                            {/* Información de stock */}
                                                            <div style={{ 
                                                                marginTop: '8px', 
                                                                padding: '4px 8px', 
                                                                borderRadius: '6px',
                                                                background: stockInsuficiente ? '#fecaca' : '#d1fae5',
                                                                fontSize: '11px',
                                                                fontWeight: '600'
                                                            }}>
                                                                {stockInsuficiente ? (
                                                                    <span style={{ color: '#dc2626' }}>
                                                                        <FontAwesomeIcon icon={faExclamationTriangle} /> 
                                                                        Stock: {stockActual} (Faltan {cantidad - stockActual})
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: '#059669' }}>
                                                                        <FontAwesomeIcon icon={faCheckCircle} /> 
                                                                        Stock: {stockActual}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Información adicional del producto */}
                                                            {pr.descripcion && (
                                                                <div style={{ 
                                                                    marginTop: '6px', 
                                                                    fontSize: '10px', 
                                                                    color: '#6b7280',
                                                                    maxHeight: '40px',
                                                                    overflow: 'hidden'
                                                                }}>
                                                                    {pr.descripcion.substring(0, 100)}...
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="global-empty-state" style={{ width: '100%' }}>
                                            <FontAwesomeIcon icon={faBox} size="3x" />
                                            <div className="global-empty-title">Sin productos</div>
                                            <div className="global-empty-description">
                                                Este pedido no tiene ítems para mostrar.
                                            </div>
                                        </div>
                                    )}

                                    {/* Resumen de totales */}
                                    {pedidoItems.length > 0 && (
                                        <div style={{
                                            marginTop: '24px',
                                            padding: '16px',
                                            background: '#f8fafc',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            <h4 style={{ marginBottom: '12px', color: '#374151' }}>Resumen del Pedido</h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                <div>
                                                    <strong>Total de items:</strong> {pedidoItems.length}
                                                </div>
                                                <div>
                                                    <strong>Cantidad total:</strong> {
                                                        pedidoItems.reduce((acc, item) => acc + Number(item.cantidad || 0), 0)
                                                    }
                                                </div>
                                                <div>
                                                    <strong>Subtotal bruto:</strong> {
                                                        formatMoney(pedidoItems.reduce((acc, item) => {
                                                            return acc + (Number(item.precio_unitario || 0) * Number(item.cantidad || 0));
                                                        }, 0))
                                                    }
                                                </div>
                                                <div>
                                                    <strong>Descuentos:</strong> {
                                                        formatMoney(pedidoItems.reduce((acc, item) => {
                                                            return acc + Number(item.precio_descuento || 0);
                                                        }, 0))
                                                    }
                                                </div>
                                                <div style={{ 
                                                    gridColumn: '1 / -1', 
                                                    borderTop: '1px solid #d1d5db', 
                                                    paddingTop: '8px',
                                                    marginTop: '8px'
                                                }}>
                                                    <strong style={{ fontSize: '16px', color: '#355C7D' }}>
                                                        Total del pedido: {formatMoney(pedido.total)}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer acciones */}
                        <div className="product-submit-section">
                            <button className="btn-product-secondary" onClick={handleDownloadPDF}>
                                <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: 6 }} />
                                Descargar PDF
                            </button>
                            <button className="btn-product-secondary" onClick={cerrarModal}>
                                Cerrar
                            </button>
                            <button
                                className="btn-product-primary"
                                onClick={() => handleGuardarEstado(pedido.idPedido)}
                                disabled={isLocked(pedido)}
                            >
                                <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 6 }} />
                                Guardar Cambios
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Paginación inferior */}
            {paginacion.total_paginas > 1 && (
                <div className="flex-between" style={{ marginTop: 24, padding: '0 16px' }}>
                    <div className="text-muted">
                        Mostrando {Math.min(limite, paginacion.total_registros)} de {paginacion.total_registros} registros
                    </div>
                    <div className="flex-center" style={{ gap: 8 }}>
                        <button 
                            className="btn-product-secondary" 
                            onClick={() => cambiarPagina(1)}
                            disabled={paginaActual === 1}
                        >
                            Primera
                        </button>
                        <button 
                            className="btn-product-secondary" 
                            onClick={() => cambiarPagina(paginaActual - 1)}
                            disabled={!paginacion.tiene_anterior}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span style={{ 
                            padding: '8px 16px', 
                            background: '#355C7D', 
                            color: 'white', 
                            borderRadius: '6px',
                            fontWeight: 600 
                        }}>
                            {paginaActual} / {paginacion.total_paginas}
                        </span>
                        <button 
                            className="btn-product-secondary" 
                            onClick={() => cambiarPagina(paginaActual + 1)}
                            disabled={!paginacion.tiene_siguiente}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                        <button 
                            className="btn-product-secondary" 
                            onClick={() => cambiarPagina(paginacion.total_paginas)}
                            disabled={paginaActual === paginacion.total_paginas}
                        >
                            Última
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}